import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "../components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";

// Define types for LLP contribution data
type IndividualPartner = {
  id: string;
  id_type: string;
  name: string;
  designation: string;
  obligation_contribution: number;
  received_contribution: number;
};

type BodyCorporate = {
  id: string;
  id_type: string;
  name: string;
  obligation_contribution: number;
  received_contribution: number;
  nominee_id?: string;
  nominee_name?: string;
  nominee_id_type?: string;
};

type ContributionDetails = {
  financial_year: string;
  metadata?: {
    doc_id?: string;
    xml_to_pdf_converted_doc_id?: string | null;
  };
  individual_partners?: IndividualPartner[];
  body_corporates?: BodyCorporate[];
};

interface LLPContributionDetailsProps {
  contributionDetails?: ContributionDetails | ContributionDetails[];
}

// Format currency values
const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(value);
};

export default function LLPContributionDetails({ 
  contributionDetails 
}: LLPContributionDetailsProps) {
  
  // Normalize to array - handle both single object and array
  const detailsArray = Array.isArray(contributionDetails) 
    ? contributionDetails 
    : contributionDetails 
      ? [contributionDetails] 
      : [];
  
  if (!contributionDetails || detailsArray.length === 0) {
    return (
      <div className="py-12 text-center text-gray-500">
        No contribution details available for this LLP.
      </div>
    );
  }

  return (
    <div className="space-y-6 ">
      <div>
        <h2 className="text-2xl font-bold mb-2">LLP Contribution Details</h2>
        <p className="text-gray-600">View partner contributions and body corporate details</p>
      </div>

      {detailsArray.map((yearData, yearIndex) => {
        const hasIndividualPartners = yearData.individual_partners && yearData.individual_partners.length > 0;
        const hasBodyCorporates = yearData.body_corporates && yearData.body_corporates.length > 0;
        const hasData = hasIndividualPartners || hasBodyCorporates;

        if (!hasData) {
          return (
            <Card key={yearIndex} className="mb-6 bg-white">
              <CardHeader>
                <CardTitle>Financial Year: {yearData.financial_year}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="py-8 text-center text-gray-500">
                  No contribution data available for this financial year.
                </div>
              </CardContent>
            </Card>
          );
        }

        return (
          <div key={yearIndex} className="space-y-6">
            <Card className="bg-white">
              <CardHeader>
                <CardTitle>Financial Year: {yearData.financial_year}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Individual Partners Table */}
                {hasIndividualPartners && (
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Individual Partners</h3>
                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-gray-50">
                            <TableHead className="font-semibold">Name</TableHead>
                            <TableHead className="font-semibold">DIN</TableHead>
                            <TableHead className="font-semibold">Designation</TableHead>
                            <TableHead className="text-right font-semibold">Obligation Contribution</TableHead>
                            <TableHead className="text-right font-semibold">Received Contribution</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {yearData.individual_partners!.map((partner, index) => (
                            <TableRow 
                              key={index}
                              className="hover:bg-gray-50 transition-colors"
                            >
                              <TableCell className="font-medium">{partner.name}</TableCell>
                              <TableCell>{partner.id}</TableCell>
                              {/* <TableCell>{partner.id_type}</TableCell> */}
                              <TableCell>{partner.designation}</TableCell>
                              <TableCell className="text-right">
                                {formatCurrency(partner.obligation_contribution)}
                              </TableCell>
                              <TableCell className="text-right">
                                {formatCurrency(partner.received_contribution)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

                {/* Body Corporates Table */}
                {hasBodyCorporates && (
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Body Corporates</h3>
                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-gray-50">
                            <TableHead className="font-semibold">Name</TableHead>
                            <TableHead className="font-semibold">CIN</TableHead>
                            <TableHead className="font-semibold">Nominee Name</TableHead>
                            <TableHead className="font-semibold">DIN</TableHead>
                            <TableHead className="text-right font-semibold">Obligation Contribution</TableHead>
                            <TableHead className="text-right font-semibold">Received Contribution</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {yearData.body_corporates!.map((corporate, index) => (
                            <TableRow 
                              key={index}
                              className="hover:bg-gray-50 transition-colors"
                            >
                              <TableCell className="font-medium">{corporate.name}</TableCell>
                              <TableCell>{corporate.id}</TableCell>
                              <TableCell>{corporate.nominee_name || '-'}</TableCell>
                              <TableCell>{corporate.nominee_id || '-'}</TableCell>
                              <TableCell className="text-right">
                                {formatCurrency(corporate.obligation_contribution)}
                              </TableCell>
                              <TableCell className="text-right">
                                {formatCurrency(corporate.received_contribution)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        );
      })}
    </div>
  );
}

